import { Alert, Button, Icon, Typography } from '@sravni/react-design-system';
import { ArrowRight } from '@sravni/react-icons';
import { useIsMobile } from '@sravni/react-utils';
import cn from 'classnames';
import type { ComponentProps } from 'react';
import React, { useCallback } from 'react';

import type { FormStepId } from 'shared/config/formStepId';
import { useAppDispatch, useAppSelector } from 'shared/lib/redux';
import { AdaptiveTitleText } from 'shared/ui/AdaptiveText';
import { AuthenticationEsiaGrid, AuthenticationEsiaIcon } from 'shared/ui/AuthenticationESIAButton';

import { EsiaTextsConfig } from '../../AuthenticationEsia.texts';
import { readDataAfterEsiaSignInThunk } from '../../lib';
import {
  shouldUpdateEsiaSelector,
  showLoginByEsiaSelector,
  showEsiaErrorSelector,
} from '../../model/AuthenticationEsia.selectors';

import styles from './AuthenticationEsia.module.scss';

interface IAuthenticationEsia {
  // идентификатор шага формы
  step: FormStepId;
  config?: { title: string; titleMobile?: string; subtitle?: string; buttonTitle?: string };
  className?: string;
  buttonProps?: Omit<ComponentProps<typeof Button>, 'onClick'>;
  isShowIcon?: boolean;
  isForceShowEsia?: boolean;
}

export const AuthenticationEsia: FC<IAuthenticationEsia> = (props) => {
  const { step, config, className, buttonProps, isShowIcon = true, isForceShowEsia = false } = props;
  const dispatch = useAppDispatch();
  const isMobile = useIsMobile();
  const { isEsiaAvailable, isWl, subtitle, title, buttonTitle, titleMobile } = useAppSelector(
    showLoginByEsiaSelector(step),
  );
  const shouldUpdateSet = useAppSelector(shouldUpdateEsiaSelector);
  const isEsiaError = useAppSelector(showEsiaErrorSelector(step));
  const finalTitle = isMobile ? (config?.titleMobile ?? titleMobile ?? title) : (config?.title ?? title);
  const finalSubtitle = config?.subtitle || subtitle;
  const finalButtonTitle = config?.buttonTitle || buttonTitle;

  const handleClickOnEsiaEnter = useCallback(() => {
    dispatch(readDataAfterEsiaSignInThunk(step));
  }, [dispatch, step]);

  if (isEsiaError) {
    return (
      <Alert
        color="orange"
        title={EsiaTextsConfig.error.title}
        subtitle={EsiaTextsConfig.error.subtitle}
      />
    );
  }

  if ((!isEsiaAvailable || !shouldUpdateSet[step]) && !isForceShowEsia) {
    return null;
  }

  if (isWl) {
    return (
      <Typography.Link
        onClick={handleClickOnEsiaEnter}
        className={styles.link}
      >
        <AuthenticationEsiaGrid
          gap={12}
          className={styles.wlEsiaBlock}
          isButtonRight
          icon={
            <AuthenticationEsiaIcon
              background="light"
              className={styles.wlEsiaIcon}
            />
          }
          button={<Icon icon={<ArrowRight />} />}
          title={<AdaptiveTitleText range={[16, 16]}>{finalTitle}</AdaptiveTitleText>}
          subtitle={
            <AdaptiveTitleText
              range={[14, 14]}
              strong={false}
            >
              {finalSubtitle}
            </AdaptiveTitleText>
          }
        />
      </Typography.Link>
    );
  }

  return (
    <AuthenticationEsiaGrid
      onClick={isMobile ? handleClickOnEsiaEnter : undefined}
      className={cn(styles.defaultEsiaBlock, className)}
      icon={isShowIcon ? <AuthenticationEsiaIcon /> : undefined}
      title={<AdaptiveTitleText range={[14, 16]}>{finalTitle}</AdaptiveTitleText>}
      subtitle={
        finalSubtitle ? (
          <AdaptiveTitleText
            range={[14, 14]}
            strong={false}
          >
            {finalSubtitle}
          </AdaptiveTitleText>
        ) : undefined
      }
      isButtonRight={isMobile}
      button={
        isMobile ? (
          <Icon
            icon={<ArrowRight />}
            color="blue"
          />
        ) : (
          <Button
            variant="primary"
            size={isMobile ? 36 : 52}
            onClick={handleClickOnEsiaEnter}
            color="blue"
            block={isMobile}
            {...buttonProps}
          >
            {finalButtonTitle}
          </Button>
        )
      }
    />
  );
};
